const SITE_NAME = 'Razzberry';
const SITE_URL = 'https://razzberry.ai';
const DEFAULT_DESCRIPTION = 'Razzberry is a platform where music and art become limited digital collectibles with real ownership.';
const DEFAULT_TITLE = 'Razzberry | Limited Digital Music & Art Collectibles';
const DEFAULT_IMAGE = `${SITE_URL}/assets/razzberry-white-logo-500.png`;

function trimSlash(value) {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

function ensureLeadingSlash(value = '/') {
  return value.startsWith('/') ? value : `/${value}`;
}

export function ensureCanonicalUrl(pathname = '/') {
  const safePath = ensureLeadingSlash(pathname);
  return safePath === '/' ? `${SITE_URL}/` : `${trimSlash(SITE_URL)}${safePath}`;
}

function upsertMeta({ attr, key, content }) {
  if (typeof document === 'undefined') return;
  let node = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!node) {
    node = document.createElement('meta');
    node.setAttribute(attr, key);
    document.head.appendChild(node);
  }
  node.setAttribute('content', content);
}

function upsertLink({ rel, href }) {
  if (typeof document === 'undefined') return;
  let node = document.head.querySelector(`link[rel="${rel}"]`);
  if (!node) {
    node = document.createElement('link');
    node.setAttribute('rel', rel);
    document.head.appendChild(node);
  }
  node.setAttribute('href', href);
}

export function truncateDescription(value, limit = 160) {
  const text = String(value || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit - 1).trimEnd()}…`;
}

export function getProductSocialImage(product = {}) {
  return String(
    product?.ipfsImage?.posterUrl
    || product?.ipfsImage?.url
    || DEFAULT_IMAGE
  ).trim() || DEFAULT_IMAGE;
}

export function buildProductCanonicalPath(productId = '') {
  return `/products/${String(productId || '').trim()}`;
}

export function buildProductMetadata({ product, productId, artist }) {
  const resolvedProductId = String(productId || product?.id || product?.productId || '').trim();
  const pathname = buildProductCanonicalPath(resolvedProductId);
  const productTitle = String(product?.title || '').trim() || 'Collectible Release';
  const artistName = String(artist?.artistFullName || '').trim();
  const description = truncateDescription(
    product?.description || `${productTitle}${artistName ? ` by ${artistName}` : ''} on Razzberry.`,
    160
  );

  return {
    title: `${productTitle} | Razzberry`,
    description,
    pathname,
    canonical: ensureCanonicalUrl(pathname),
    robots: 'index,follow',
    type: 'product',
    image: getProductSocialImage(product),
  };
}

export function applyMetadata({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  pathname = '/',
  canonical,
  robots = 'index,follow',
  image = DEFAULT_IMAGE,
  type = 'website',
}) {
  if (typeof document === 'undefined') return;
  const resolvedCanonical = canonical || ensureCanonicalUrl(pathname);
  const resolvedDescription = truncateDescription(description || DEFAULT_DESCRIPTION);
  const resolvedTitle = String(title || DEFAULT_TITLE).trim() || DEFAULT_TITLE;

  document.title = resolvedTitle;

  upsertMeta({ attr: 'name', key: 'description', content: resolvedDescription });
  upsertMeta({ attr: 'name', key: 'application-name', content: SITE_NAME });
  upsertMeta({ attr: 'name', key: 'apple-mobile-web-app-title', content: SITE_NAME });
  upsertMeta({ attr: 'name', key: 'robots', content: robots });

  upsertMeta({ attr: 'property', key: 'og:site_name', content: SITE_NAME });
  upsertMeta({ attr: 'property', key: 'og:type', content: type });
  upsertMeta({ attr: 'property', key: 'og:title', content: resolvedTitle });
  upsertMeta({ attr: 'property', key: 'og:description', content: resolvedDescription });
  upsertMeta({ attr: 'property', key: 'og:url', content: resolvedCanonical });
  upsertMeta({ attr: 'property', key: 'og:image', content: image });

  upsertMeta({ attr: 'name', key: 'twitter:card', content: 'summary_large_image' });
  upsertMeta({ attr: 'name', key: 'twitter:title', content: resolvedTitle });
  upsertMeta({ attr: 'name', key: 'twitter:description', content: resolvedDescription });
  upsertMeta({ attr: 'name', key: 'twitter:image', content: image });

  upsertLink({ rel: 'canonical', href: resolvedCanonical });
}

export function getDefaultRouteMetadata(pathname) {
  if (pathname === '/') {
    return {
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
      robots: 'index,follow',
    };
  }

  if (pathname === '/signin') {
    return {
      title: 'Sign In | Razzberry',
      description: 'Sign in to Razzberry to access your digital collectibles, artist releases, and collector account.',
      robots: 'noindex,nofollow',
    };
  }

  if (pathname === '/signup') {
    return {
      title: 'Create Account | Razzberry',
      description: 'Create a Razzberry account to collect limited digital music and art releases with real ownership.',
      robots: 'noindex,nofollow',
    };
  }

  if (pathname === '/forgot') {
    return {
      title: 'Reset Password | Razzberry',
      description: 'Reset your Razzberry password to regain access to your artist or collector account.',
      robots: 'noindex,nofollow',
    };
  }

  if (pathname.startsWith('/artists/')) {
    return {
      title: 'Artist | Razzberry',
      description: 'Discover artist releases, digital collectibles, and public profiles on Razzberry.',
      robots: 'index,follow',
      type: 'profile',
    };
  }

  if (pathname.startsWith('/products/') && !pathname.endsWith('/preview')) {
    return {
      title: 'Collectible Release | Razzberry',
      description: 'Discover collectible releases, drop timing, pricing, and artist details on Razzberry.',
      robots: 'index,follow',
      type: 'product',
    };
  }

  return {
    title: SITE_NAME,
    description: DEFAULT_DESCRIPTION,
    robots: 'noindex,nofollow',
  };
}

export function buildArtistMetadata({ artist, artistId }) {
  const artistName = String(artist?.artistFullName || '').trim() || 'Artist';
  const artistDescription = truncateDescription(
    artist?.artistDescription || `${artistName} on Razzberry. Discover limited digital collectibles with real ownership.`,
    160
  );

  return {
    title: `${artistName} | Razzberry`,
    description: artistDescription,
    pathname: `/artists/${artistId}`,
    robots: 'index,follow',
    type: 'profile',
  };
}

export { DEFAULT_DESCRIPTION, DEFAULT_IMAGE, DEFAULT_TITLE, SITE_NAME, SITE_URL };
